import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { ZoomDialog } from './zoom-dialog.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const CLICK_ZOOM = 4;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    this.#setupDialogCloseListener();

    // Init cho cả mobile lẫn desktop nếu đang trong dialog
    const isInDialog = !!this.closest('dialog');
    if (isMobileBreakpoint() || isInDialog) {
      this.#initEventListeners();
      this.#updateTransform();
    }
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #setupDialogCloseListener() {
    const zoomDialog = /** @type {ZoomDialog} */ (this.closest('zoom-dialog'));
    if (!zoomDialog || typeof zoomDialog.close !== 'function') return;

    const originalClose = zoomDialog.close.bind(zoomDialog);

    zoomDialog.close = async (...args) => {
      this.#resetZoom();
      return await originalClose(...args);
    };
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const touchOptions = { passive: false, signal };
    const mouseOptions = { signal };

    // Touch events (mobile)
    this.addEventListener('touchstart', this.#handleTouchStart, touchOptions);
    this.addEventListener('touchmove', this.#handleTouchMove, touchOptions);
    this.addEventListener('touchend', this.#handleTouchEnd, touchOptions);

    // Mouse events (desktop)
    this.addEventListener('mousedown', this.#handleMouseDown, mouseOptions);
    this.addEventListener('mousemove', this.#handleMouseMove, mouseOptions);
    this.addEventListener('mouseup', this.#handleMouseUp, mouseOptions);
    this.addEventListener('mouseleave', this.#handleMouseLeave, mouseOptions);

    // Wheel zoom (desktop)
    this.addEventListener('wheel', this.#handleWheel, { passive: false, signal });

    this.#updateTransform();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  // ─── TOUCH HANDLERS (mobile) ──────────────────────────────────────────────

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      this.#storeTapInfo(performance.now(), touch);
      this.#startDragGesture(touch.clientX, touch.clientY);
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;
    console.log(touchCount, "_touchCount")
    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch.clientX, touch.clientY);
      }
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      const wasDragging = this.#hasDraggedBeyondThreshold;
      const wasPinching = this.#hasManualZoom;

      event.stopPropagation();

      this.#isDragging = false;
      this.#hasDraggedBeyondThreshold = false;

      if (!wasDragging && !wasPinching && event.changedTouches.length === 1) {
        const touch = event.changedTouches[0];
        if (touch) {
          this.#handleSingleTap(touch.clientX, touch.clientY);
        }
      }

      this.#requestUpdateTransform();
    }
  };

  // ─── MOUSE HANDLERS (desktop) ─────────────────────────────────────────────

  /**
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    // Chỉ xử lý left click
    if (event.button !== 0) return;
    preventDefault(event);

    this.#startDragGesture(event.clientX, event.clientY);
    this.style.cursor = 'grabbing';
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;
    preventDefault(event);

    this.#processDragGesture(event.clientX, event.clientY);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseUp = (event) => {
    if (!this.#isDragging) return;

    const wasDragging = this.#hasDraggedBeyondThreshold;

    event.stopPropagation();

    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    this.style.cursor = this.#scale > MIN_ZOOM ? 'grab' : 'zoom-in';

    // Click đơn (không drag) → toggle zoom
    if (!wasDragging) {
      this.#handleSingleTap(event.clientX, event.clientY);
    }

    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseLeave = (event) => {
    if (this.#isDragging) {
      this.#isDragging = false;
      this.#hasDraggedBeyondThreshold = false;
      this.style.cursor = this.#scale > MIN_ZOOM ? 'grab' : 'zoom-in';
      this.#requestUpdateTransform();
    }
  };

  /**
   * Wheel zoom tại vị trí con trỏ chuột
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    preventDefault(event);

    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    const oldScale = this.#scale;
    const newScale = clamp(oldScale * delta, MIN_ZOOM, MAX_ZOOM);

    if (newScale === oldScale) return;

    this.#scale = newScale;
    this.#hasManualZoom = true;

    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = event.clientX - (this.getBoundingClientRect().left + containerCenterX);
    const distanceFromCenterY = event.clientY - (this.getBoundingClientRect().top + containerCenterY);

    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    // Reset hasManualZoom nếu về 1x
    if (this.#scale <= MIN_ZOOM) {
      this.#hasManualZoom = false;
      this.style.cursor = 'zoom-in';
    } else {
      this.style.cursor = 'grab';
    }

    this.#requestUpdateTransform();
  };

  // ─── SHARED GESTURE LOGIC ─────────────────────────────────────────────────

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  #startDragGesture(clientX, clientY) {
    this.#startPosition = { x: clientX, y: clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Toggle zoom 1x ↔ 2x tại vị trí click/tap
   * Khi đang pinch manual zoom thì reset về 1x
   * @param {number} clientX
   * @param {number} clientY
   */
  #handleSingleTap(clientX, clientY) {
    const tolerance = 0.05;
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    // Nếu đang ở zoom thủ công (pinch/wheel), reset về 1x
    if (this.#hasManualZoom) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
      this.style.cursor = 'zoom-in';
      this.#requestUpdateTransform();
      return;
    }

    if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
      // Đang 1x → zoom lên 2x tại vị trí click
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, CLICK_ZOOM);

      const distanceFromCenter = {
        x: clientX - (this.getBoundingClientRect().left + containerCenter.x),
        y: clientY - (this.getBoundingClientRect().top + containerCenter.y),
      };

      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

      this.style.cursor = 'grab';
    } else {
      // Đang 2x → về 1x
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.style.cursor = 'zoom-in';
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;

    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    this.#hasManualZoom = true;

    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  #processDragGesture(clientX, clientY) {
    const distance = Math.sqrt(
      Math.pow(clientX - this.#startPosition.x, 2) +
      Math.pow(clientY - this.#startPosition.y, 2)
    );

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    // Chỉ cho drag khi đang zoom (scale > 1x)
    if (this.#scale <= MIN_ZOOM) return;

    this.#hasDraggedBeyondThreshold = true;

    const dx = clientX - this.#startPosition.x;
    const dy = clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  // ─── TRANSFORM ────────────────────────────────────────────────────────────

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  #resetZoom() {
    this.#scale = DEFAULT_ZOOM;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;

    this.style.cursor = 'zoom-in';
    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * @param {Point | Touch} point1
 * @param {Point | Touch} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */